import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { translations } from "@/i18n/translations";
import {
  getSettings,
  changeLanguage as persistLanguage,
} from "@/store/store";

const I18nContext = createContext(null);

const SUPPORTED = ["pl", "en"];
const DEFAULT_LANG = "pl";

/**
 * Resolve a dot-path key against a dictionary object.
 * Returns the key itself if the path can't be resolved,
 * so missing translations are visible instead of empty.
 */
function resolveKey(dict, key) {
  if (!dict) return key;
  const parts = key.split(".");
  let node = dict;
  for (const part of parts) {
    if (node && Object.prototype.hasOwnProperty.call(node, part)) {
      node = node[part];
    } else {
      return key;
    }
  }
  return node;
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const stored = getSettings().language;
    return SUPPORTED.includes(stored) ? stored : DEFAULT_LANG;
  });

  // Reflect the current language on <html lang="…"> for a11y and update
  // the document title so it stays language-appropriate.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
      document.title =
        language === "pl"
          ? "Kompas Dnia — Daily Compass AI"
          : "Daily Compass — AI Decision Dashboard";
    }
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED.includes(lang)) return;
    persistLanguage(lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key, params) => {
      const value = resolveKey(translations[language], key);
      const text = typeof value === "string" ? value : (value ?? key);
      if (typeof text !== "string" || !params) return text;
      return text.replace(/\{(\w+)\}/g, (_, k) =>
        Object.prototype.hasOwnProperty.call(params, k)
          ? String(params[k])
          : `{${k}}`,
      );
    },
    [language],
  );

  const tArray = useCallback(
    (key) => {
      const value = resolveKey(translations[language], key);
      return Array.isArray(value) ? value : [];
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, tArray }),
    [language, setLanguage, t, tArray],
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider />");
  }
  return ctx;
}
